import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CommonTable from '../../components/commonComponent/CommanTable';
import UserView from './UserView';
import CommonModal from '../../components/commonComponent/CommonModal';
import { FaEye, FaEdit, FaPlus } from 'react-icons/fa';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from '../../utils/alertHelper';

// User Table Columns
const userFields = [
  { key: 'srNo', label: 'Sr.no' },
  { key: 'userId', label: 'User ID' },
  { key: 'name', label: 'Name' },
  { key: 'emailId', label: 'Email id' },
  { key: 'roleName', label: 'Role' }, // Show role name
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Action' },
];

// Role Table Columns
const roleFields = [
  { key: 'srNo', label: 'Sr.no' }, 
  { key: 'roleName', label: 'Role' },
  { key: 'description', label: 'Description' },
  { key: 'actions', label: 'Action' },
];

function UserRole() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('user');
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalData, setUserModalData] = useState({
    firstName: '',
    lastName: '',
    emailId: '',
    roleId: '',
    status: 'Active',
    note: '',
    userType: localStorage.getItem("userType") || 'Company',
  });
  const [userErrors, setUserErrors] = useState({});
  const [userSubmitting, setUserSubmitting] = useState(false);

  // Table Data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [viewUserModal, setViewUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [totalUsers, setTotalUsers] = useState(0);

  // Check for tab query parameter on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam === 'role') {
      setActiveTab('role');
    }
  }, [location.search]);

  useEffect(() => {
    // Fetch roles and default role options once
    fetchRoles();
    fetchDefaultRoles();
  }, []);

  useEffect(() => {
    // Fetch users when page or limit change
    fetchUsers(page, limit);
  }, [page, limit]);

  // Fetch roles from API using axios
  async function fetchRoles() {
    try {
      const businessId = localStorage.getItem('businessId');
      let url = `/admin/api/role`;
      if (businessId) {
        url += `?business=${businessId}`;
      }
      const res = await axios.get(url);
      const data = res.data;
      if (data.Status === 1 && Array.isArray(data.roles)) {
        setRoles(data.roles.map((r, idx) => ({
          id: r._id,
          roleName: r.name,
          description: r.description || '',
          createdAt: r.createdAt,
          updatedAt: r.updatedAt,
        })));
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }

  // Fetch default roles for user creation form
  async function fetchDefaultRoles() {
    try {
      const businessId = localStorage.getItem('businessId');
      let url = `/admin/api/role`;
      if (businessId) {
        url += `?business=${businessId}`;
      }
      const res = await axios.get(url);
      const data = res.data;
      if (data.Status === 1 && Array.isArray(data.roles)) {
        setRoleOptions(data.roles.map(r => ({
          value: r._id,
          label: r.name,
          description: r.description || ""
        })));
      }
    } catch (err) {
      console.error('Error fetching default roles:', err);
    }
  }

  // --- THIS IS THE USER MAPPING PART TO INCLUDE ROLE NAME ---
  async function fetchUsers(pageArg = page, limitArg = limit) {
    try {
      const userType = localStorage.getItem("userType");
      const storedBusinessId = localStorage.getItem("businessId");
      // Build query parameters
      const params = new URLSearchParams();
      if (userType) params.append('userType', userType);
      if (storedBusinessId) params.append('business', storedBusinessId);
      params.append('page', pageArg);
      params.append('limit', limitArg);
      const url = `/api/user?${params.toString()}`;
      const res = await axios.get(url);
      const data = res.data;
      if (data.Status === 1 && Array.isArray(data.users)) {
        setUsers(data.users.map(u => {
          // Find role name from roleOptions if needed
          let roleName = '';
          if (u.role && typeof u.role === 'object' && u.role.name) {
            roleName = u.role.name;
          } else if (u.role) {
            const foundRole = roleOptions.find(r => r.value === u.role || r.value === u.role._id);
            roleName = foundRole ? foundRole.label : '';
          }
          return {
            id: u._id,
            userId: u.memberId || '',
            firstName: u.firstName,
            lastName: u.lastName,
            name: `${u.firstName} ${u.lastName}`,
            emailId: u.email,
            roleId: u.role?._id || u.role || '', // For form usage
            roleName, // Show in table
            status: typeof u.status === 'object' ? u.status.name : u.status,
            note: u.note || '',
            userType: u.userType || '',
          };
        }));
        const total = data.total || data.count || data.totalItems || data.totalCount || data.meta?.total;
        setTotalUsers(Number(total) || data.users.length || 0);
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
      setTotalUsers(0);
    }
  }

  // User Table Data
  const userTableData = users
    .filter(u =>
      u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.userId.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.emailId.toLowerCase().includes(userSearchTerm.toLowerCase())
    )
    .map((u, idx) => ({
      ...u,
      srNo: (page - 1) * limit + idx + 1,
      status: (
        <span
          className={`badge px-3 py-2 rounded-pill`}
          style={{
            backgroundColor: u.status === 'Active' ? '#28a745' : '#6c757d',
            color: '#fff',
            fontWeight: 500,
          }}
        >
          {typeof u.status === 'string' ? u.status : (u.status && u.status.name ? u.status.name : '')}
        </span>
      ),
      actions: (
        <div>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="View"
            onClick={() => {
              setSelectedUser(u);
              setViewUserModal(true);
            }}
          >
            <FaEye size={14} />
          </button>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => {
              const editData = {
                ...u,
                roleId: u.roleId,
              };
              setUserModalData(editData);
              setShowUserModal(true);
            }}
          >
            <FaEdit size={14} />
          </button>
        </div>
      ),
    }));

    useEffect(() => {
      setPage(1);
    }, [userSearchTerm]);

  // Role Table Data
  const roleTableData = roles
    .filter(r =>
      r.roleName.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
      (r.description && r.description.toLowerCase().includes(roleSearchTerm.toLowerCase()))
    )
    .map((r, idx) => ({
      ...r,
      srNo: idx + 1,
      actions: (
        <div>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => {
              navigate(`/user/userRole/edit/${r.id}`);
            }}
          >
            <FaEdit size={14} />
          </button>
        </div>
      ),
    }));

  // User Modal Submit
  const handleUserSubmit = async () => {
    const errors = {};
    ['roleId','firstName','lastName','status','emailId'].forEach(f => {
      if (!userModalData[f]) errors[f] = "Required";
    });
    setUserErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setUserSubmitting(true);

    const userType = localStorage.getItem("userType") || 'Company';
    const payload = {
      firstName: (userModalData.firstName || '').trim(),
      lastName: (userModalData.lastName || '').trim(),
      email: (userModalData.emailId || '').trim(),
      roleId: (userModalData.roleId || '').trim(),
      status: (userModalData.status || 'Active').trim(),
      note: (userModalData.note || '').trim(),
      userType: userType.trim(),
    };

    try {
      let res;
      // If editing existing user (has id), use PUT to update
      if (userModalData && userModalData.id) {
        res = await axios.put(`/api/user/${userModalData.id}`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        res = await axios.post('/api/user/create', payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      }

      const data = res.data;
      if (data.Status === 1) {
        toastSuccess(data.Message || (userModalData && userModalData.id ? 'User updated successfully!' : 'User created successfully!'));
        // Refresh users
        await fetchUsers();
        setShowUserModal(false);
        setUserModalData({
          firstName: '',
          lastName: '',
          emailId: '',
          roleId: roleOptions[0]?.value || '',
          status: 'Active',
          note: '',
          userType: localStorage.getItem("userType") || 'Company',
        });
      } else {
        const errorMessage = data.Message || (userModalData && userModalData.id ? "Failed to update user" : "Failed to create user");
        toastError(errorMessage);
        setUserErrors({ api: errorMessage });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.Message || err.message || (userModalData && userModalData.id ? "Failed to update user" : "Failed to create user");
      toastError(errorMessage);
      setUserErrors({ api: errorMessage });
    }
    setUserSubmitting(false);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="h2 mb-0">User & Role</h2>
        <div className="d-flex gap-2 align-items-center">
          {activeTab === 'user' ? (
            <button className="btn btn-primary" onClick={() => { 
              const currentUserType = localStorage.getItem("userType") || 'Company';
              setUserModalData({
                firstName: '',
                lastName: '',
                emailId: '',
                roleId: roleOptions[0]?.value || '',
                status: 'Active',
                note: '',
                userType: currentUserType,
              }); 
              setShowUserModal(true); 
            }}>
              <FaPlus className="me-1" /> Create New User
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => navigate('/user/userRole/create')}>
              <FaPlus className="me-1" /> Create New Role
            </button>
          )}

        </div>
      </div>

      {/* Tabs */}
      <div className="mb-3">
        <button
          className={`btn px-3 py-2 me-2 ${
            activeTab === 'user' ? "btn-primary" : "btn-light"
          }`}
          style={{ borderRadius: "6px", fontWeight: 500 }}
          onClick={() => setActiveTab('user')}
        >
          User {activeTab === 'user' && `(${users.length})`}
        </button>
        <button
          className={`btn px-3 py-2 me-2 ${
            activeTab === 'role' ? "btn-primary" : "btn-light"
          }`}
          style={{ borderRadius: "6px", fontWeight: 500 }}
          onClick={() => setActiveTab('role')}
        >
          Role {activeTab === 'role' && `(${roles.length})`}
        </button>
      </div>

      {activeTab === 'user' ? (
        <>
          {/* Search Bar */}
          <div className="row mb-4">
            <div className="col-md-6"></div>
            <div className="col-md-6 d-flex justify-content-end">
              <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Type to search..."
                  value={userSearchTerm}
                  onChange={e => setUserSearchTerm(e.target.value)}
                  style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </div>
          </div>
          {/* User Table */}
          <CommonTable
            columns={userFields}
            data={userTableData}
            itemsPerPageOptions={[10, 15, 25, 50]}
            defaultItemsPerPage={15}
            page={page}
            onPageChange={setPage}
            itemsPerPage={limit}
            onItemsPerPageChange={setLimit}
            totalItems={totalUsers}
          />
          {/* User Modal */}
          <CommonModal
            visible={showUserModal}
            title={userModalData && userModalData.id ? "Edit User" : "Add New User"}
            fields={[
              { name: 'firstName', label: 'First Name', type: 'text', placeholder: 'First Name' },
              { name: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Last Name' },
              { name: 'emailId', label: 'Email ID', type: 'text', placeholder: 'Email ID' },
              { name: 'roleId', label: 'Role Id', type: 'select', options: roleOptions, placeholder: 'Select Role' },
              { name: 'status', label: 'Status', type: 'select', options: [
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' }
                ], placeholder: 'Select Status'
              },
              { name: 'note', label: 'Note', type: 'textarea', placeholder: 'Note', maxLength: 500 },
            ]}
            values={userModalData}
            onChange={setUserModalData}
            onCancel={() => {
              setShowUserModal(false);
              setUserModalData({
                firstName: '',
                lastName: '',
                emailId: '',
                roleId: roleOptions[0]?.value || '',
                status: 'Active',
                note: '',
                userType: localStorage.getItem("userType") || 'Company',
              });
              setUserErrors({});
            }}
            onSubmit={handleUserSubmit}
            submitLabel={userSubmitting ? (userModalData && userModalData.id ? "Updating..." : "Saving...") : (userModalData && userModalData.id ? "Update" : "Save")}
            cancelLabel="Cancel"
            requiredFields={['roleId','firstName','lastName','status','emailId']}
            errors={userErrors}
            disabled={userSubmitting}
          />

          <UserView
            visible={viewUserModal}
            user={selectedUser}
            onClose={() => setViewUserModal(false)}
          />
        </>
      ) : (
        <>
          {/* Search Bar */}
          <div className="row mb-4">
            <div className="col-md-6"></div>
            <div className="col-md-6 d-flex justify-content-end">
              <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Type to search..."
                  value={roleSearchTerm}
                  onChange={e => setRoleSearchTerm(e.target.value)}
                  style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </div>
          </div>
          {/* Role Table */}
          <CommonTable
            columns={roleFields}
            data={roleTableData}
            itemsPerPageOptions={[10, 15, 25, 50]}
            defaultItemsPerPage={15}
            page={page}
            onPageChange={setPage}
            itemsPerPage={limit}
            onItemsPerPageChange={setLimit}
          />
        </>
      )}
    </div>
  );
}

export default UserRole;